import type { DateComponents, DateTimeAnswerValue, TimeComponents } from './types.no-deps'
import { assumeExhaustion } from '../../lib/typeHelpers.no-deps'
import { oneMinuteInMs } from '../../lib/duration.no-deps'

export function getDateComponents(date: Date): DateComponents {
    return {
        day: date.getDate(),
        month: date.getMonth() + 1,
        year: date.getFullYear(),
    }
}

export function getTimeComponents(date: Date): TimeComponents {
    return {
        hour: date.getHours(),
        minute: date.getMinutes(),
    }
}

export function isValidDateComponents({ year, month, day }: DateComponents): boolean {
    if (year == null || month == null || day == null) return false

    // 275760 years is 8,640,000,000,000,000ms after 1970
    // 100 years is the first date where the 2 digit day rule does not apply
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#two_digit_years_map_to_1900_%E2%80%93_1999
    // https://262.ecma-international.org/5.1/#sec-15.9.1.1
    if (100 > year || year > 275760) return false

    if (1 > month || month > 12) return false
    if (1 > day || day > 31) return false
    return true
}

export function isValidTimeComponents({ hour, minute }: TimeComponents): boolean {
    if (hour == null || minute == null) return false
    if (0 > hour || hour > 23) return false
    if (0 > minute || minute > 59) return false
    return true
}

export function dateToAnswerValue(date: Date, selectType: 'date' | 'time' | 'datetime'): DateTimeAnswerValue {
    const dateComponents = getDateComponents(date)
    const timeComponents = getTimeComponents(date)
    const timezoneOffsetInMs = date.getTimezoneOffset() * oneMinuteInMs
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

    switch (selectType) {
        case 'datetime':
            return {
                type: 'datetime',
                date: dateComponents,
                time: timeComponents,
                timezoneOffsetInMs,
                timezone,
            }
        case 'time':
            return {
                type: 'time',
                time: timeComponents,
                timezoneOffsetInMs,
                timezone,
            }
        case 'date':
            return {
                type: 'date',
                date: dateComponents,
                timezoneOffsetInMs,
                timezone,
            }
        default:
            assumeExhaustion(selectType)
    }
}

/**
 * Create a Date object from DateTimeAnswerValue, falling back to the report start date for missing components
 */
export function answerValueToDate(answerValue: DateTimeAnswerValue, reportStartDate: Date) {
    // Get the fallback components from working out what the report start date would look to them
    const fallbackComponents = getApparentDateInTimezone(reportStartDate, answerValue.timezoneOffsetInMs)

    // Get the components we have and falling back the missing ones
    const { year, month, day } = answerValue.type !== 'time' ? answerValue.date : fallbackComponents
    const { hour, minute } = answerValue.type !== 'date' ? answerValue.time : fallbackComponents

    // Copying seconds and milliseconds from report start date
    // as it should be same for default answer value for seconds and milliseconds
    const seconds = reportStartDate.getSeconds()
    const milliseconds = reportStartDate.getMilliseconds()

    // Create a date with offset
    const offset = answerValue.timezoneOffsetInMs ?? 0
    return new Date(Date.UTC(year, month - 1, day, hour, minute, seconds, milliseconds) + offset)
}

/**
 * What would a Date look like to someone in their timezone offset
 */
function getApparentDateInTimezone(date: Date, timezoneOffsetInMs: number | undefined) {
    date = new Date(date.getTime() - (timezoneOffsetInMs ?? 0))
    return {
        day: date.getUTCDate(),
        month: date.getUTCMonth() + 1,
        year: date.getUTCFullYear(),
        hour: date.getUTCHours(),
        minute: date.getUTCMinutes(),
    }
}
